import { CommonModule, NgFor, NgIf } from '@angular/common'
import { Component, inject, OnDestroy, OnInit } from '@angular/core'
import { FormGroup, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { Business } from '../../../../store/business/business.state'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'
import { select, Store } from '@ngrx/store'
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  startWith,
  Subject,
  take,
  takeUntil,
  tap
} from 'rxjs'
import { loadBusinesses } from '../../../../store/business/business.actions'
import { selectBusinesses } from '../../../../store/business/business.reducer'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { RouterModule } from '@angular/router'
import { eps } from '../../../../services/url.service'
import { environment } from '../../../../../environments/environment'
import { selectUser } from '../../../../store/auth/auth.state'
import { InvoiceComponent } from '../invoice/invoice.component'
import {
  selectContractorPayrollData,
  selectRunningPayroll
} from '../../../../store/contractor-payroll/contractor-payroll.reducers'
import {
  ContractorPayrollStatus,
  IncomingContractorPayroll
} from '../../../../store/contractor-payroll/contractor-payroll.state'
import { runningCPActions } from '../../../../store/contractor-payroll/contractor-payroll.actions'
import { PayoutFrequency } from '../../../../store/payout-agreement/payout-agreement.state'
import dayjs from 'dayjs'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import weekOfYear from 'dayjs/plugin/weekOfYear'
dayjs.extend(quarterOfYear)
dayjs.extend(weekOfYear)

@Component({
  selector: 'app-payslip-wrapper',
  imports: [
    NgIf,
    NgFor,
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    CapitalizePipe
  ],
  templateUrl: './invoice-wrapper.component.html',
  styleUrl: './invoice-wrapper.component.scss'
})
export class InvoiceWrapperComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private modalService = inject(NgbModal)
  private destroy$ = new Subject<void>()
  PayoutFrequency = PayoutFrequency
  eps = eps
  imageBaseUrl = environment.apiBaseUrl + '/uploads/'
  currentLoggedInUserRole = ''
  currentLoggedInUserId = ''
  currentLoggedInUserBusinessId = ''
  InvoiceComponent = InvoiceComponent
  runningPayroll$ = this.store.select(selectRunningPayroll)
  financialYears: { value: number; label: string }[] = []
  filterForm = new FormGroup({
    selectedBusiness: new FormControl('', { nonNullable: true }),
    payoutFrequency: new FormControl<PayoutFrequency | ''>('', { nonNullable: true }),
    selectedMonth: new FormControl('', { nonNullable: true }),
    selectedYear: new FormControl(this.getCurrentFinancialYearStart(), { nonNullable: true }),
    selectedPeriod: new FormControl('', { nonNullable: true }),
    selectedDate: new FormControl('', { nonNullable: true })
  })
  selectedMonth = new FormControl('', { nonNullable: true })
  payPeriodStartDate = ''
  payPeriodEndDate = ''
  selectedBusiness = new FormControl('', { nonNullable: true })
  businessesList: Business[] = []
  selectedPayrollIndex = new FormControl(null)
  searchTerm = ''
  private searchTerm$ = new BehaviorSubject<string>('')
  filteredpayrolls$!: Observable<IncomingContractorPayroll[]>
  payrolls: IncomingContractorPayroll[] = []

  constructor() {
    this.generateFinancialYears()
  }

  ngOnInit(): void {
    this.store.dispatch(runningCPActions.clearRunningPayroll())
    this.payrolls = []
    this.listenForFilterChanges()

    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe(user => {
        this.currentLoggedInUserRole = user && user.userRoles ? user?.userRoles[0].role?.name : ''
        this.currentLoggedInUserId = user?.id ?? ''
        this.currentLoggedInUserBusinessId = user?.businessId ?? ''
        if (
          this.currentLoggedInUserRole === 'Business Owner' ||
          this.currentLoggedInUserRole === 'Admin'
        ) {
          this.loadBusinessesList()
        } else {
          this.selectedBusiness.setValue(this.currentLoggedInUserBusinessId)
        }
        this.setupPayrollDataPipeline()
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  // Helper to get employee name initials
  getInitials(firstName = '', lastName = ''): string {
    const firstInitial = firstName.charAt(0)
    const lastInitial = lastName.charAt(0)
    return (firstInitial + lastInitial).toUpperCase()
  }

  private loadBusinessesList(): void {
    this.store.dispatch(loadBusinesses({}))
    this.store
      .pipe(
        select(selectBusinesses),
        filter(businesses => !!businesses && businesses.length > 0),
        takeUntil(this.destroy$)
      )
      .subscribe(businesses => {
        this.businessesList = businesses
        if (businesses.length === 1) {
          this.selectedBusiness.setValue(businesses[0].id)
        }
      })
  }

  // Helper to generate a list of financial years for the UI
  private generateFinancialYears(): void {
    let currentYear = new Date().getFullYear()
    // Go back 5 years and ahead 1 year
    for (let i = 5; i >= -1; i--) {
      const startYear = currentYear - i
      const endYear = startYear + 1
      this.financialYears.push({
        value: startYear,
        label: `${startYear}-${endYear.toString()}` // e.g., "FY 2023-24"
      })
    }
  }

  // Helper to get the start year of the current financial year
  private getCurrentFinancialYearStart(): number {
    const today = dayjs()
    // If the month is before April (index 3), the financial year started last calendar year.
    return today.month() < 3 ? today.year() - 1 : today.year()
  }

  private listenForFilterChanges(): void {
    this.filterForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        // debounceTime(300),
        tap(() => {
          // Reset data whenever filters change
          this.selectedPayrollIndex.setValue(null)
          this.payrolls = []
        })
      )
      .subscribe(formValue => {
        const {
          selectedBusiness,
          payoutFrequency,
          selectedMonth,
          selectedYear,
          selectedPeriod,
          selectedDate
        } = formValue

        // Only proceed if we have the required base info
        if (!selectedBusiness || !payoutFrequency) {
          this.store.dispatch(runningCPActions.clearRunningPayroll())
          return
        }

        // Calculate Date Range based on Payout Frequency
        this.calculateDateRange(payoutFrequency, {
          selectedMonth,
          selectedYear,
          selectedPeriod,
          selectedDate
        })

        // Dispatch the action if we have a valid date range
        if (this.payPeriodStartDate && this.payPeriodEndDate) {
          if (this.currentLoggedInUserRole === 'Employee') {
            //this.store.dispatch(
            //  userPayrollActions.loadUserPayroll({
            //    payPeriodStart: this.payPeriodStartDate,
            //    payPeriodEnd: this.payPeriodEndDate,
            //    employeeId: this.currentLoggedInUserId
            //  })
            //)
          } else {
            this.store.dispatch(
              runningCPActions.loadRunningPayroll({
                payPeriodStart: this.payPeriodStartDate,
                payPeriodEnd: this.payPeriodEndDate,
                belongsTo: selectedBusiness,
                payoutFrequency: payoutFrequency
              })
            )
          }
        }
      })
  }

  // Helper function to calculate date ranges
  private calculateDateRange(frequency: PayoutFrequency, values: any): void {
    const { selectedMonth, selectedYear, selectedPeriod, selectedDate } = values
    let start, end

    // Ensure selectedYear is a number
    const year = Number(selectedYear)
    if (isNaN(year)) return

    switch (frequency) {
      case PayoutFrequency.MONTHLY:
        if (!selectedMonth) return
        start = dayjs(selectedMonth, 'YYYY-MM').startOf('month')
        end = start.endOf('month')
        break

      case PayoutFrequency.ANNUALLY:
        // Financial Year: April 1 of selectedYear to March 31 of selectedYear + 1
        start = dayjs().year(year).month(3).startOf('month') // April 1st
        end = dayjs()
          .year(year + 1)
          .month(2)
          .endOf('month') // March 31st
        break

      case PayoutFrequency.SEMI_ANNUALLY:
        if (!selectedPeriod) return
        if (selectedPeriod === 'h1') {
          // H1: April 1 to September 30
          start = dayjs().year(year).month(3).startOf('month') // April 1st
          end = dayjs().year(year).month(8).endOf('month') // September 30th
        } else {
          // 'h2'
          // H2: October 1 to March 31
          start = dayjs().year(year).month(9).startOf('month') // October 1st
          end = dayjs()
            .year(year + 1)
            .month(2)
            .endOf('month') // March 31st (next year)
        }
        break

      case PayoutFrequency.QUARTERLY:
        if (!selectedPeriod) return
        const quarter = Number(selectedPeriod)
        if (quarter === 1) {
          // Q1: April-June
          start = dayjs().year(year).month(3).startOf('month')
          end = dayjs().year(year).month(5).endOf('month')
        } else if (quarter === 2) {
          // Q2: July-September
          start = dayjs().year(year).month(6).startOf('month')
          end = dayjs().year(year).month(8).endOf('month')
        } else if (quarter === 3) {
          // Q3: October-December
          start = dayjs().year(year).month(9).startOf('month')
          end = dayjs().year(year).month(11).endOf('month')
        } else {
          // Q4: January-March
          start = dayjs()
            .year(year + 1)
            .month(0)
            .startOf('month')
          end = dayjs()
            .year(year + 1)
            .month(2)
            .endOf('month')
        }
        break

      case PayoutFrequency.WEEKLY:
        if (!selectedDate) return
        start = dayjs(selectedDate).startOf('week')
        end = start.endOf('week')
        break

      case PayoutFrequency.BI_WEEKLY:
        if (!selectedDate) return
        const date = dayjs(selectedDate)
        const weekOfYear = date.week()
        const weekStart = date.startOf('week')
        // An implementation of bi-weekly: odd weeks are starts of pay periods.
        start = weekOfYear % 2 === 1 ? weekStart : weekStart.subtract(1, 'week')
        end = start.add(13, 'days').endOf('day') // 14-day period
        break

      case PayoutFrequency.DAILY:
        if (!selectedDate) return
        start = dayjs(selectedDate).startOf('day')
        end = start.endOf('day')
        break
    }

    if (start && end && start.isValid() && end.isValid()) {
      this.payPeriodStartDate = start.format('YYYY-MM-DD')
      this.payPeriodEndDate = end.format('YYYY-MM-DD')
    } else {
      this.payPeriodStartDate = ''
      this.payPeriodEndDate = ''
    }
  }

  private setupPayrollDataPipeline(): void {
    // 1. Create a source observable based on the user's role
    const payrollsSource$: Observable<IncomingContractorPayroll[]> =
      this.currentLoggedInUserRole === 'Employee'
        ? this.store
            .select(selectContractorPayrollData)
            .pipe(
              map(payroll =>
                payroll && payroll.status === ContractorPayrollStatus.PAID ? [payroll] : []
              )
            )
        : this.store.select(selectRunningPayroll).pipe(
            map(runningPayroll => {
              if (runningPayroll && runningPayroll.status === ContractorPayrollStatus.PAID) {
                const payrolls = [
                  ...(runningPayroll.contractorPayrolls.fixed ?? []),
                  ...(runningPayroll.contractorPayrolls.hourly ?? [])
                ]
                return payrolls
              }
              return []
            })
          )

    // 2. Combine the source with the search term to create the final filtered list
    this.filteredpayrolls$ = combineLatest([
      payrollsSource$,
      this.searchTerm$.pipe(startWith(this.searchTerm))
    ]).pipe(
      map(([payrolls, term]) => {
        // Keep a copy of the full list for the modal
        this.payrolls = payrolls

        if (!term?.trim()) {
          return payrolls // No search term, return everything
        }
        const lowerCaseTerm = term.toLowerCase()
        return payrolls.filter(payroll =>
          `${payroll.employee.firstName} ${payroll.employee.lastName}`
            .toLowerCase()
            .includes(lowerCaseTerm)
        )
      })
    )
  }

  openModal(modal: unknown, payroll: IncomingContractorPayroll) {
    const modalRef = this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'lg'
    })
    if (modalRef.componentInstance) {
      console.log('payroll', payroll)
      modalRef.componentInstance.payrollData = payroll
      modalRef.componentInstance.payPeriodStartDate = this.payPeriodStartDate
      modalRef.componentInstance.payPeriodEndDate = this.payPeriodEndDate
      modalRef.componentInstance.download = true
    }
  }

  onSearchChange(): void {
    this.searchTerm$.next(this.searchTerm)
  }
}
